import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Check } from "lucide-react";
import { t } from "@/content/i18n/messages";
import { getLocalizedPathname, type Locale } from "@/lib/i18n";
import {
  ASUS_GX10_OFFER_ID,
  ASUS_GX10_TERMS,
  asusGx10Configurations,
  asusGx10QuotePath,
  formatGx10Price,
} from "@/content/enterprise/asus-gx10-offer";

export function AsusGx10Offer({ locale }: { locale: Locale }) {
  return (
    <section
      id="gx10-quotation"
      aria-labelledby="gx10-offer-heading"
      className="mt-12 scroll-mt-24 overflow-hidden rounded-2xl border border-border bg-card p-5 sm:p-8"
    >
      <div className="grid items-center gap-8 lg:grid-cols-[0.8fr_1fr]">
        <Image
          src="/images/enterprise/asus-gx10-quotation-offer.png"
          alt={t(locale, "ASUS Ascent GX10 quotation offer: 1 TB RM38,000, 2 TB RM46,000, 4 TB RM49,000.")}
          width={1122}
          height={1402}
          className="order-2 mx-auto h-auto w-full max-w-md rounded-xl lg:order-1"
          loading="lazy"
        />
        <div className="order-1 lg:order-2">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">
            ASUS Ascent GX10 · 128 GB
          </p>
          <h2 id="gx10-offer-heading" className="mt-3 font-display text-2xl font-bold text-neutral-emphasis sm:text-3xl">
            {t(locale, "Choose your GX10 configuration")}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-neutral-muted">
            {t(locale, "Select your storage to request a quotation. We will confirm the exact SKU, availability and final price.")}
          </p>
          <ul className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-neutral-muted">
            {["NVIDIA GB10", "128 GB unified memory", "1-year hardware warranty"].map((item) => (
              <li key={item} className="flex items-center gap-2">
                <Check className="h-4 w-4 text-primary" aria-hidden />
                {t(locale, item)}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-neutral-muted">
            {t(locale, "Advertised price per unit")}
          </p>
          <ul className="mt-3 space-y-3">
            {asusGx10Configurations.map((option) => (
              <li key={option.storage}>
                <Link
                  href={getLocalizedPathname(asusGx10QuotePath(option.storage), locale)}
                  data-track-event="cta_click"
                  data-track-location="asus_gx10_configuration"
                  data-track-id={`${ASUS_GX10_OFFER_ID}-${option.storage.replace(" ", "")}`}
                  className="flex items-center justify-between gap-4 rounded-xl border border-primary/20 px-5 py-4 transition-colors hover:border-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                >
                  <span>
                    <span className="block text-sm font-semibold text-neutral-emphasis">{option.storage}</span>
                    <span className="mt-1 block text-xl font-bold text-primary">{formatGx10Price(option.priceRm)}</span>
                  </span>
                  <span className="flex items-center gap-2 text-sm font-semibold text-primary">
                    {t(locale, "Request quotation")}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs leading-relaxed text-neutral-muted">{t(locale, ASUS_GX10_TERMS)}</p>
        </div>
      </div>
    </section>
  );
}
